import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, Inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { isEqual } from "lodash-es";
import { TextareaModule } from "primeng/textarea";
import { ButtonComponent } from "../../../../../../../shared/components/button/button.component";
import {
  TicketBatch,
  TicketInBatch,
} from "../../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import { DialogHeaderComponent } from "../../../../../../../shared/components/dialog-header/dialog-header.component";
import {
  buildHalfPriceTicket,
  defaultHalfPriceAmount,
  defaultHalfPriceQuantity,
  halfPriceTitle,
} from "../../../../../../../shared/helpers/half-price/half-price.helper";
import {
  buildModalTicketForm,
  ModalTicketForm,
} from "./ticket-edit-modal.form";

export interface TicketEditDialogData {
  ticketBatch: TicketBatch;
  ticket?: TicketInBatch;
  isTicketFree: boolean;
}

@Component({
  selector: "app-ticket-edit-modal",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextareaModule,
    ButtonComponent,
    MatDialogModule,
    DialogHeaderComponent,
  ],
  templateUrl: "./ticket-edit-modal.component.html",
  styleUrl: "./ticket-edit-modal.component.scss",
})
export class TicketEditModalComponent implements OnInit {
  ticketBatch: TicketBatch;
  ticket?: TicketInBatch;
  isTicketFree: boolean;

  ticketForm!: ModalTicketForm;

  private readonly dialogRef = inject(MatDialogRef<TicketEditModalComponent>);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  constructor(@Inject(MAT_DIALOG_DATA) public data: TicketEditDialogData) {
    this.ticketBatch = data.ticketBatch;
    this.ticket = data.ticket;
    this.isTicketFree = data.isTicketFree;
  }

  private isEditMode = false;
  private editingTicketIndex: number | null = null;

  get showsHalfPricePair(): boolean {
    return (
      !!this.ticketBatch.has_half_price &&
      !this.isTicketFree &&
      !this.ticket?.half_price
    );
  }

  ngOnInit(): void {
    this.setupTicketModal();
    this.syncHalfPriceDefaults();
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  saveTicket(): void {
    if (!this.ticketForm.valid) {
      this.ticketForm.markAllAsTouched();
      return;
    }

    const ticketFormValues = this.ticketForm.getRawValue();
    const numericPrice = Number(ticketFormValues.price);

    const mappedTicket: TicketInBatch = {
      id: ticketFormValues.id ?? this.ticket?.id ?? undefined,
      batch_id: this.ticketBatch.id ?? this.ticket?.batch_id,
      title: ticketFormValues.ticketTitle,
      description: ticketFormValues.ticketDescription,
      min_per_buyer: ticketFormValues.minTickets,
      max_per_buyer: ticketFormValues.maxTickets,
      quantity: ticketFormValues.quantity,
      price: Number.isFinite(numericPrice) ? numericPrice : 0,
      availability: ticketFormValues.availability,
      half_price: this.ticket?.half_price ?? ticketFormValues.halfPrice,
      parent_ticket_id: this.ticket?.parent_ticket_id,
    };

    if (this.isEditMode) {
      if (this.editingTicketIndex !== null) {
        const previousTicket =
          this.ticketBatch.tickets[this.editingTicketIndex];

        this.ticketBatch.tickets[this.editingTicketIndex] = {
          ...previousTicket,
          ...mappedTicket,
        };
        this.upsertHalfPriceTicket(
          this.ticketBatch.tickets[this.editingTicketIndex],
        );
      }
    } else {
      this.ticketBatch.tickets.push(mappedTicket);
      this.upsertHalfPriceTicket(mappedTicket);
    }

    this.dialogRef.close(this.ticketBatch);
  }

  private setupTicketModal() {
    this.isEditMode = !!this.ticket;

    // garante array
    if (!Array.isArray(this.ticketBatch.tickets)) {
      this.ticketBatch.tickets = [];
    }

    if (this.isEditMode) {
      this.editingTicketIndex = this.findEditingTicketIndex();
    }

    this.ticketForm = buildModalTicketForm({
      formBuilder: this.formBuilder,
      ticket: this.ticket,
      halfTicket: this.findPairedHalfTicket(this.ticket),
      isTicketFree: this.isTicketFree,
      withHalfPricePair: this.showsHalfPricePair,
    });
  }

  private upsertHalfPriceTicket(fullTicket: TicketInBatch): void {
    if (!this.showsHalfPricePair) return;

    const ticketFormValues = this.ticketForm.getRawValue();
    const halfPrice = Number(ticketFormValues.halfPricePrice);
    const halfQuantity = Number(ticketFormValues.halfPriceQuantity);
    const nextHalf: TicketInBatch = {
      ...buildHalfPriceTicket(fullTicket, {
        price: Number.isFinite(halfPrice)
          ? halfPrice
          : defaultHalfPriceAmount(fullTicket.price),
        quantity: Number.isFinite(halfQuantity)
          ? halfQuantity
          : defaultHalfPriceQuantity(fullTicket.quantity),
      }),
      id: ticketFormValues.halfPriceId ?? undefined,
      description: fullTicket.description,
      min_per_buyer: fullTicket.min_per_buyer,
      max_per_buyer: fullTicket.max_per_buyer,
      availability: fullTicket.availability,
    };

    const existingIndex = this.ticketBatch.tickets.findIndex((item) => {
      if (!item.half_price) return false;
      if (fullTicket.id && item.parent_ticket_id === fullTicket.id) return true;
      if (nextHalf.id != null && item.id === nextHalf.id) return true;
      if (item.title === halfPriceTitle(fullTicket.title)) return true;
      if (this.ticket && item.title === halfPriceTitle(this.ticket.title)) {
        return true;
      }
      return false;
    });

    if (existingIndex >= 0) {
      this.ticketBatch.tickets[existingIndex] = {
        ...this.ticketBatch.tickets[existingIndex],
        ...nextHalf,
        id: this.ticketBatch.tickets[existingIndex].id ?? nextHalf.id,
      };
      return;
    }

    this.ticketBatch.tickets.push(nextHalf);
  }

  private findPairedHalfTicket(
    fullTicket: TicketInBatch | undefined,
  ): TicketInBatch | undefined {
    if (!fullTicket || fullTicket.half_price) return undefined;

    return this.ticketBatch.tickets.find(
      (item) =>
        item.half_price &&
        ((fullTicket.id && item.parent_ticket_id === fullTicket.id) ||
          item.title === halfPriceTitle(fullTicket.title)),
    );
  }

  private syncHalfPriceDefaults(): void {
    if (!this.showsHalfPricePair || this.isEditMode) return;

    this.ticketForm.controls.quantity.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((quantity) => {
        if (!this.ticketForm.controls.halfPriceQuantity.pristine) return;
        this.ticketForm.controls.halfPriceQuantity.setValue(
          defaultHalfPriceQuantity(Number(quantity)),
          { emitEvent: false },
        );
      });

    this.ticketForm.controls.price.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((price) => {
        if (!this.ticketForm.controls.halfPricePrice.pristine) return;
        this.ticketForm.controls.halfPricePrice.setValue(
          defaultHalfPriceAmount(Number(price)),
          { emitEvent: false },
        );
      });
  }

  private findEditingTicketIndex(): number | null {
    if (!this.ticket) return null;

    if (this.ticket.id !== undefined) {
      const indexById = this.ticketBatch.tickets.findIndex(
        (existingTicket) => existingTicket.id === this.ticket!.id,
      );
      if (indexById >= 0) {
        return indexById;
      }
    }

    // 2) Por referência (funciona se o array tiver o mesmo objeto)
    const indexByReference = this.ticketBatch.tickets.indexOf(this.ticket);
    if (indexByReference >= 0) {
      return indexByReference;
    }

    const indexBySignature = this.ticketBatch.tickets.findIndex(
      (existingTicket) => isEqual(this.ticket, existingTicket),
    );

    return indexBySignature >= 0 ? indexBySignature : null;
  }

  getTaxString(source: "price" | "halfPricePrice" = "price"): string {
    const taxRate = 0.1;
    const ticketPrice = Number(this.ticketForm.getRawValue()[source]);
    const taxValue = ticketPrice * taxRate;
    if (this.ticketBatch.pdv_only) {
      return `O cliente paga R$${ticketPrice} no PDV; as taxas são absorvidas pelo produtor`;
    }
    if (this.absorbsPlatformFee) {
      return `O cliente paga R$${ticketPrice}; a taxa da plataforma de R$${taxValue} é absorvida pelo produtor`;
    }

    return `R$${ticketPrice} + Taxa(R$${taxValue})`;
  }

  getFinalPriceString(source: "price" | "halfPricePrice" = "price"): number {
    const taxRate = 0.1;
    const ticketPrice = Number(this.ticketForm.getRawValue()[source]);
    const taxValue = ticketPrice * taxRate;
    return this.ticketBatch.pdv_only || this.absorbsPlatformFee ? ticketPrice : ticketPrice + taxValue;
  }

  get absorbsPlatformFee(): boolean {
    return !!this.ticketBatch.absorbs_platform_fee;
  }
}
